/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import {
  calculateFundamentalScore,
  calculatePnlPercentage,
  classNames,
  formatPrice,
  formatISTDateTime,
} from "../utils/utils";
import { useTrade } from "../hooks/useTrade";
import OrderModal from "./OrderModal";
import TradingSymbol from "./TradingSymbol";
import { AuthContext } from "../contexts/AuthContext";
import api from "services/api";
import { watchlistService } from "../services/watchlistService";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { IconButton } from "@mui/material";

interface Trade {
  _id: string;
  tradingsymbol: string;
  instrumentKey: string;
  instrument_token: string;
  strategyName: string;
  currentPrice: number;
  last_price: number;
  entry: number;
  target: number;
  stopLoss: number;
  timestamp: Date;
  action: string;
  atr: number;
  volume: number;
  sector: string;
  tick_size: number;
  peRatio: number;
  roe?: number;
  debtToEquity?: number;
  technicalScore: number;
  fundamentalScore: number;
  backtestScore: number;
  compositeScore: number;
  priceActionScore: number;
  signalBucket: string;
  riskLevel: string;
  fundamentals?: {
    summaryDetail: {
      trailingPE: number;
      dividendYield: number;
    };
    defaultKeyStatistics: {
      earningsQuarterlyGrowth: number;
      returnOnEquity?: number;
    };
    financialData: {
      debtToEquity: number;
      revenueGrowth: number;
      returnOnEquity?: number;
    };
  };
  newsFlag?: boolean;
  strategies: { strategyName: string; signal?: string }[];
  marketCap?: number;
  capCategory?: string;
  fiftyTwoWeekHigh?: number;
  fiftyTwoWeekLow?: number;
  crossover?: string;
  fiftyMA?: number;
  twoHundredMA?: number;
  activeSignal: string;
  daysActive: number;
  rsi?: number;
  prevClose?: number;
  watchlistedBy: string[];
}

const WatchlistStar = ({ row, userId }: { row: Trade; userId: string | undefined }) => {
  const [isStarred, setIsStarred] = useState(
    userId ? row.watchlistedBy?.includes(userId) : false
  );

  const handleToggle = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userId) return;
    try {
      const res = await watchlistService.toggleWatchlist(row.tradingsymbol);
      setIsStarred(res.isWatchlisted);
    } catch (error) {
      console.error("Failed to toggle watchlist:", error);
    }
  }, [userId, row.tradingsymbol]);

  return (
    <IconButton onClick={handleToggle} size="small" color="primary">
      {isStarred ? <StarIcon /> : <StarBorderIcon />}
    </IconButton>
  );
};

const ScannerTable = ({
  results,
  isLoading,
  prices,
}: {
  results: Trade[];
  isLoading: boolean;
  prices: Record<number, { price: number; ohlcClose?: number, change?: number }>;
}) => {
  const [activeStrategy, setActiveStrategy] = useState<string | null>(null);
  const [tradeModal, setTradeModal] = useState<any>({});
  const { confirmTrade } = useTrade();
  // const [data, setData] = useState(results);
  const [refreshKey, setRefreshKey] = useState(0);
  const authContext = useContext(AuthContext);
  const isAdmin = authContext?.isAdmin || false;


  // useEffect(() => {
  //   results.forEach((stock) => {
  //     if (stock.fundamentals) {
  //       stock.fundamentalScore = calculateFundamentalScore(stock.fundamentals);
  //       // EPS Growth Check (bonus if >10%)
  //       if (
  //         stock.fundamentals?.defaultKeyStatistics?.earningsQuarterlyGrowth >
  //         0.1
  //       ) {
  //         stock.fundamentalScore += 10; // Bonus for strong EPS growth
  //         stock.fundamentalScore = Math.min(100, stock.fundamentalScore); // Cap at 100
  //       }
  //     }
  //     stock.newsFlag = stock.fundamentalScore > 60;

  //     const nonLaggingWeights = {
  //       "Hull Moving Average": 30,
  //       VWAP: 24,
  //       "Support Bounce": 10,
  //       "Heikin-Ashi": 18,
  //     };
  //     const laggingWeights = {
  //       // "RSI Oversold": 5,
  //       "MA Crossover": 5,
  //       "MA Bounce": 5,
  //       "Bollinger Band Touch": 5,
  //       "ATR Volatility": 5,
  //     };
  //     const trendStratergy = {
  //       Uptrend: 10,
  //     };
  //     let score = 0;
  //     // let nonLaggingCount = 0;
  //     // let buyNonLagging = 0;
  //     // let sellNonLagging = 0;

  //     stock.strategies.forEach((s) => {
  //       if (nonLaggingWeights[s.strategyName]) {
  //         score += nonLaggingWeights[s.strategyName];
  //         // nonLaggingCount++;
  //         // if (s.signal === "Buy") buyNonLagging++;
  //         // else if (s.signal === "Sell") sellNonLagging++;
  //       } else if (laggingWeights[s.strategyName]) {
  //         score += laggingWeights[s.strategyName];
  //       } else if (trendStratergy[s.strategyName]) {
  //         score += 10;
  //       }
  //       //  else {
  //       //   score -= 10;
  //       // }
  //     });

  //     // ATR modifier
  //     if (stock.atr && stock.atr > 6) {
  //       score -= 10;
  //     } else if (stock.atr && stock.atr >= 2 && stock.atr <= 6) {
  //       score += 5;
  //     }

  //     // Enhanced mixed signal penalty
  //     // if (sellNonLagging >= buyNonLagging) score -= 15;

  //     // Confluence bonus for Buy
  //     // if (buyNonLagging >= 2) score += 5;

  //     stock.technicalScore = Math.max(0, Math.min(100, score));
  //   });
  //   // setData(updatedData);
  // }, [results, refreshKey]);

  // useEffect(() => {
  //   // Fetch backtest data once
  //   const fetchBacktestData = async () => {
  //     try {
  //       const backtestMap: Record<string, any> = {};
  //       const res = await fetchBackTestResults();
  //       const data = res;
  //       console.log("backtest data", data);

  //       data?.forEach((d: any) => (backtestMap[d.symbol] = d));

  //       results.forEach((stock) => {
  //         // === Fundamental Score ===
  //         if (stock.fundamentals) {
  //           stock.fundamentalScore = calculateFundamentalScore(
  //             stock.fundamentals
  //           );

  //           // Bonus for EPS growth >10%
  //           if (
  //             stock.fundamentals?.defaultKeyStatistics
  //               ?.earningsQuarterlyGrowth > 0.1
  //           ) {
  //             stock.fundamentalScore = Math.min(
  //               100,
  //               stock.fundamentalScore + 10
  //             );
  //           }
  //         }

  //         // === Technical (Live) Score ===
  //         const nonLaggingWeights = {
  //           "Hull Moving Average": 30,
  //           VWAP: 24,
  //           "Support Bounce": 10,
  //           "Heikin-Ashi": 18,
  //         };
  //         const laggingWeights = {
  //           "MA Crossover": 5,
  //           "MA Bounce": 5,
  //           "Bollinger Band Touch": 5,
  //           "ATR Volatility": 5,
  //         };
  //         const trendWeights = { Uptrend: 10 };
  //         let techScore = 0;

  //         stock.strategies.forEach((s) => {
  //           if (nonLaggingWeights[s.strategyName])
  //             techScore += nonLaggingWeights[s.strategyName];
  //           else if (laggingWeights[s.strategyName])
  //             techScore += laggingWeights[s.strategyName];
  //           else if (trendWeights[s.strategyName]) techScore += 10;
  //         });

  //         // ATR adjustment
  //         if (stock.atr > 6) techScore -= 10;
  //         else if (stock.atr >= 2 && stock.atr <= 6) techScore += 5;

  //         // === Backtest (Historical) Quality Score ===
  //         const backtest = backtestMap[stock.tradingsymbol];
  //         // console.log("backtestMap", backtestMap);

  //         let backtestScore = 0;
  //         if (backtest) {
  //           console.log(`backtest for ${stock.tradingsymbol}:`, backtest);

  //           // Normalize: Sharpe (weighted), Win rate, Drawdown penalty
  //           const sharpe = Math.max(0, backtest.sharpeRatio);
  //           const win = backtest.winRate * 100;
  //           const ddPenalty = Math.min(100, backtest.maxDrawdown * 100);

  //           backtestScore = sharpe * 20 + win * 0.5 - ddPenalty * 0.5;
  //           backtestScore = Math.max(0, Math.min(100, backtestScore));
  //         }

  //         // === Composite Weighted Formula ===
  //         // 50% technical + 30% fundamental + 20% backtest
  //         const composite =
  //           techScore * 0.5 +
  //           (stock.fundamentalScore || 0) * 0.3 +
  //           backtestScore * 0.2;

  //         stock.technicalScore = Math.round(
  //           Math.max(0, Math.min(100, composite))
  //         );
  //         stock.newsFlag = stock.technicalScore >= 60; // highlight
  //       });

  //       setRefreshKey((prev) => prev + 1);
  //     } catch (err) {
  //       console.error("Error fetching backtest summary:", err);
  //     }
  //   };

  //   fetchBacktestData();
  // }, [results]);

  const isAllStrategies = !activeStrategy;

  const allStrategies = Array.from(
    new Set(
      results.flatMap((stock) => stock.strategies.map((s) => s.strategyName))
    )
  );

  const closeTradeModal = () => {
    setTradeModal({ open: false });
  };

  const filteredData = useMemo(() => {
    return isAllStrategies
      ? results.map((stock) => {
          const strategyNames = stock.strategies
            .map((s) => s.strategyName)
            .join(", ");
          return {
            ...stock,
            strategyCount: stock.strategies.length,
            strategyName: strategyNames,
            id: `${stock._id}-all`,
            technicalScore: stock.technicalScore, // Ensure score propagates
          };
        })
      : results
          .filter((stock) =>
            stock.strategies.some((s) => s.strategyName === activeStrategy)
          )
          .map((stock) => {
            const strategy = stock.strategies.find(
              (s) => s.strategyName === activeStrategy
            )!;
            return {
              ...stock,
              ...strategy,
              id: `${stock._id}-${strategy.strategyName}`,
              technicalScore: stock.technicalScore,
            };
          });
  }, [results, isAllStrategies, activeStrategy]);

  const startTrade = (item: Trade) => {
    setTradeModal({
      open: true,
      tradingsymbol: item.tradingsymbol,
      lastPrice: item.last_price,
      instrumentToken: item.instrument_token,
      atr: item.atr,
      volume: item.volume,
      tickSize: item.tick_size,
    });
  };

  const Stratergies = {
    // Uptr: "warning",
    // Stoc: "primary",
    Hull: "secondary",
    Heik: "secondary",
    VWAP: "secondary",
    Pric: "error",
    // Boll: "default",
    // Atr: "default",
    // "MA B": "default",
  };

  const columns: GridColDef<Trade>[] = useMemo(() => [
    {
      field: "watchlistedBy",
      headerName: "",
      width: 55,
      renderCell: (params) => (
        <WatchlistStar row={params.row} userId={authContext?.user?.id} />
      ),
    },
    {
      field: "tradingsymbol",
      headerName: "Symbol",
      flex: 0.5,
      renderCell: (params) => (
        <TradingSymbol symbol={params.row.tradingsymbol} />
      ),
    },
  
    // {
    //   field: "marketCap",
    //   headerName: "Cap (Cr)",
    //   flex: 0.4,
    //   renderCell: (params) => {
    //     const cap = params.row.marketCap;
    //     if (!cap) return <div>-</div>;
    //     const cr = cap / 10000000;
    //     return <div>{cr.toLocaleString(undefined, { maximumFractionDigits: 0 })} Cr</div>;
    //   },
    // },
     
    {
      field: "compositeScore",
      headerName: "Score",
      width: 70,
      renderCell: (params) => <>{params.row.compositeScore}</>,
    },
    // {
    //   field: "rsi",
    //   headerName: "RSI",
    //   width: 60,
    //   renderCell: (params) => (
    //     <span style={{ 
    //       color: params.row.rsi && params.row.rsi > 70 ? '#ef4444' : params.row.rsi && params.row.rsi < 30 ? '#22c55e' : 'inherit',
    //       fontWeight: (params.row.rsi && (params.row.rsi > 70 || params.row.rsi < 30)) ? 700 : 400
    //     }}>
    //       {params.row.rsi ? Math.round(params.row.rsi) : "-"}
    //     </span>
    //   ),
    // },
    // {
    //   field: "volume",
    //   headerName: "Vol (Cr)",
    //   width: 80,
    //   renderCell: (params) => {
    //     const vol = params.row.volume;
    //     if (!vol) return "-";
    //     // Convert to Crores: Volume usually represents quantity, but we want value if available
    //     // If it's pure quantity, we'll just format it with 'Cr' if it's large
    //     const inCr = vol / 10000000;
    //     return <span>{inCr.toFixed(1)} Cr</span>;
    //   },
    // },
    // {
    //   field: "technicalScore",
    //   headerName: "TS",
    //   flex: 0.2,
    //   renderCell: (params) => <>{params.row.technicalScore}</>,
    // },

    // {
    //   field: "fundamentalScore",
    //   headerName: "FS",
    //   type: "number",
    //   flex: 0.2,
    //   renderCell: (params) => <>{params.row.fundamentalScore}</>,
    // },
    // {
    //   field: "priceActionScore",
    //   headerName: "PAS",
    //   type: "number",
    //   flex: 0.2,
    //   renderCell: (params) => <>{params.row.priceActionScore}</>,
    // },
    {
      field: "backtestScore",
      headerName: "BTS",
      type: "number",
      flex: 0.2,
      renderCell: (params) => <>{Math.round(params.row.backtestScore || 0)}</>,
    },
    // {
    //   field: "daysActive",
    //   headerName: "Days",
    //   type: "number",
    //   flex: 0.15,
    //   renderCell: (params) => <>{params.row.daysActive || 0}</>,
    // },
    // {
    //   field: "last_price",
    //   headerName: "Prev Close",
    //   flex: 0.3,
    //   renderCell: (params) => {
    //     const displayPrice = params.row.prevClose || params.row.last_price;
    //     return <div>{formatPrice(Number(displayPrice))}</div>;
    //   },
    // },
    {
      field: "currentPrice",
      headerName: "Live Price",
      flex: 0.3,
      renderCell: (params) => {
        const livePrice = prices?.[Number(params.row.instrument_token)]?.price;
      const displayPrice = livePrice || params.row.currentPrice || params.row.last_price;
      const isUp = displayPrice > params.row.last_price;

      return (
        <div
          className={classNames(
            isUp ? "upColor" : "downColor",
            "percentCt"
          )}
        >
          <span className={isUp ? "priceUp" : "priceDown"}>
            {displayPrice}
          </span>
        </div>
      );
      },
    },
    {
      field: "percentage",
      headerName: "%",
      flex: 0.3,
      renderCell: (params) => {
        const token = Number(params.row.instrument_token);
        const liveData = prices?.[token];
        const livePrice = liveData?.price;
        const liveChange = liveData?.change;
        
        const baselinePrice = params.row.last_price || 0;
        const displayPrice = livePrice || params.row.currentPrice || baselinePrice;
        
        let pnl = "0.0";
        if (liveChange !== undefined) {
          pnl = liveChange.toFixed(1);
        } else if (baselinePrice > 0) {
          pnl = calculatePnlPercentage(displayPrice, baselinePrice);
        }

        const isUp = Number(pnl) >= 0;

        return (
          <div
            className={classNames(isUp ? "upColor" : "downColor", "percentCt")}
          >
            <span
              style={{
                fontSize: "12px",
                paddingLeft: "10px",
                textAlign: "right",
                float: "right",
              }}
              className={isUp ? "percentUp" : "percentDown"}
            >
              {pnl}
            </span>
          </div>
        );
      },
    },
    {
      field: "capCategory",
      headerName: "Category",
      flex: 0.4,
      renderCell: (params) => (
        <Chip
          label={params.row.capCategory === "Large Cap" ? "Large" : params.row.capCategory === "Mid Cap" ? "Mid" : "Small"}
          size="small"
          variant="outlined"
          color={
            params.row.capCategory === "Large Cap"
              ? "success"
              : params.row.capCategory === "Mid Cap"
              ? "info"
              : "warning"
          }
        />
      ),
    },
    {
      field: "crossover",
      headerName: "Cross",
      flex: 0.35,
      renderCell: (params) => (
        <Chip
          label={params.row.crossover || "None"}
          size="small"
          variant="filled"
          sx={{
            backgroundColor: params.row.crossover === "Golden"
              ? "#f2e264"
              : params.row.crossover === "Death"
                ? "#ef4444"
                : "#00000014",
                color: params.row.crossover === "Golden"
              ? "#000000"
              : params.row.crossover === "Death"
                ? "white"
                : "#000000"
          }}
        />
      ),
    },
    {
      field: "peRatio",
      headerName: "PE",
      width: 60,
      renderCell: (params) => (
        <span style={{ fontWeight: params.row.peRatio && params.row.peRatio < 25 ? 700 : 400 }}>
          {params.row.peRatio ? params.row.peRatio.toFixed(1) : "-"}
        </span>
      ),
    },
    {
      field: "roe",
      headerName: "ROE",
      width: 65,
      renderCell: (params) => (
        <span style={{ 
          color: params.row.roe && params.row.roe > 15 ? '#22c55e' : 'inherit',
          fontWeight: params.row.roe && params.row.roe > 20 ? 700 : 400
        }}>
          {params.row.roe ? `${(params.row.roe * 100).toFixed(1)}%` : "-"}
        </span>
      ),
    },
    {
      field: "debtToEquity",
      headerName: "D/E",
      width: 60,
      renderCell: (params) => (
        <span style={{ 
          color: params.row.debtToEquity && params.row.debtToEquity > 2 ? '#ef4444' : 'inherit',
          fontWeight: params.row.debtToEquity && params.row.debtToEquity < 1 ? 700 : 400
        }}>
          {params.row.debtToEquity ? params.row.debtToEquity.toFixed(2) : "-"}
        </span>
      ),
    },
    {
      field: "sector",
      headerName: "Sector",
      width: 120,
      renderCell: (params) => (
         <span style={{ fontSize: '11px', color: '#64748b' }}>{params.value || "-"}</span>
      ),
    },
    {
      field: "industry",
      headerName: "Industry",
      width: 150,
      renderCell: (params) => (
         <span style={{ fontSize: '10px', color: '#94a3b8' }}>{params.value || "-"}</span>
      ),
    },
    {
      field: "activeSignal",
      headerName: "Signal",
      flex: 0.4,
      renderCell: (params) => (
        <Chip
          label={params.row.activeSignal}
          color={
            params.row.activeSignal === 'BUY'
              ? "success"
              : params.row.activeSignal === 'SETUP'
                ? "warning"
                : params.row.activeSignal === 'WATCH'
                  ? "info"
                  : "default"
          }
        />
      )
      // <>{params.row.activeSignal}</>,
    },
    // {
    //   field: "tick_size",
    //   headerName: "Tick Size",
    //   type: "number",
    //   flex: 0.3,
    //   sortable: true,
    // },
    // {
    //   field: "strategyName",
    //   headerName: "Strategy",
    //   flex: 0.5,
    //   renderCell: (params) => (
    //     <>
    //       {params.row.strategyName.split(", ").map((name: string) => (
    //         <Chip
    //           key={name}
    //           label={name.substring(0, 4)}
    //           size="small"
    //           color={Stratergies[name.substring(0, 4)]}
    //         />
    //       ))}
    //     </>
    //   ),
    // },
    // {
    //   field: "atr",
    //   headerName: "ATR (%)",
    //   renderCell: (params) => (
    //     <span className="percent">{formatPrice(params.row?.atr)}</span>
    //   ),
    // },
    // {
    //   field: "volume",
    //   headerName: "Vol (₹ Cr)",
    //   renderCell: (params) => <>{formatPrice(params.row?.volume)}</>,
    // },
    // {
    //   field: "peRatio",
    //   headerName: "PE",
    //   renderCell: (params) => <>{params.row?.peRatio?.toFixed()}</>,
    // },
    // {
    //   field: "timestamp",
    //   headerName: "Time",
    //   flex: 0.5,
    //   valueFormatter: (params: any) => new Date(params).toDateString(),
    // },
    // {
    //   field: "fundamentalScore",
    //   headerName: "Fundamental Score",
    //   type: "number",
    //   flex: 0.5,
    //   renderCell: (params) => params.row.fundamentalScore?.toFixed(0) || "N/A",
    // },
    {
      field: "action",
      headerName: "Action",
      flex: 0.4,
      renderCell: (params) => (isAdmin &&
        <Button
          variant="outlined"
          size="small"
          onClick={() => startTrade(params.row)}
        >
          Trade
        </Button>
      ),
    },
  ], [prices, isAdmin, authContext?.user?.id]);

  // const handleRefresh = () => {
  //   setRefreshKey((prev) => prev + 1);
  // };

  // Calculate last scan time from results
  const lastScanTime = results.length > 0 
    ? new Date(Math.max(...results.map(r => new Date(r.timestamp).getTime())))
    : null;

  return (
    <Box sx={{ height: 700, width: "100%" }}>
      {lastScanTime && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'right' }}>
          Last Scan: <strong>{formatISTDateTime(lastScanTime)}</strong>
        </Typography>
      )}
      {/* <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
      >
        <Chip
          label="All Strategies"
          size="small"
          onClick={() => setActiveStrategy(null)}
          variant={!activeStrategy ? "filled" : "outlined"}
          color="primary"
        />
        {allStrategies.map((strategy: any) => (
          <Chip
            key={strategy}
            size="small"
            label={strategy}
            onClick={() => setActiveStrategy(strategy)}
            variant={activeStrategy === strategy ? "filled" : "outlined"}
            color="primary"
          />
        ))}
      </Stack> */}
      {/* <button onClick={handleRefresh}>Refresh Scores</button> */}
      {activeStrategy && filteredData.length === 0 ? (
        <Typography sx={{ p: 2 }}>
          No stocks found with the {activeStrategy} strategy
        </Typography>
      ) : (
        <DataGrid
          loading={isLoading}
          rows={filteredData}
          disableColumnFilter
          showToolbar
          columns={columns}
          getRowId={(row) => row._id}
          slots={{ toolbar: GridToolbar }}
          getRowClassName={useCallback((params: any) => {
            const row = params.row;
            // 1. Check for Pullback strategies (Support, MA Bounce, or Price Action)
            const hasPullback = row.strategies?.some((s: any) => 
              s.strategyName.includes("Support Bounce") || 
              s.strategyName.includes("MA Bounce") ||
              s.strategyName.includes("Price Action")
            );

            // 2. RSI: Include "Oversold Bounces" (25+) and "Strong Trends" (up to 75)
            const isRsiValid = row.rsi && row.rsi >= 25 && row.rsi <= 75;

            // 3. Volume (Turnover): Calculate Turnover in Crores (Price * Volume / 10^7)
            const livePrice = prices?.[Number(row.instrument_token)]?.price || row.currentPrice || row.last_price;
            const turnoverCr = (row.volume * livePrice) / 10000000;
            const isLiquid = turnoverCr > 5; // > 5 Crore turnover

            // 4. Quality: Strong Overall Score
            const isStrongScore = row.compositeScore >= 65;

            if (isStrongScore && isRsiValid && hasPullback && isLiquid) {
              return 'best-pick-row';
            }
            return '';
          }, [prices])}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } },
            sorting: { sortModel: [{ field: "compositeScore", sort: "desc" }] },
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "primary.main",
              width: "auto",
            },
          }}
        />
      )}
      {tradeModal?.open && (
        <OrderModal
          open={tradeModal.open}
          onClose={() => closeTradeModal()}
          stock={tradeModal.tradingsymbol}
          onSubmit={(data) => confirmTrade(data)}
          currentPrice={tradeModal.lastPrice}
          instrumentToken={tradeModal.instrumentToken}
          atr={tradeModal.atr}
          volume={tradeModal.volume}
          tickSize={tradeModal.tickSize}
        />
      )}
    </Box>
  );
};

export default ScannerTable;
